/**
 * SidebarPanel — the dashboard left rail.
 *
 * Port of the legacy Vue app's SideBarDashboard column: Benchmark Model,
 * Service Provider, Telemetry Connector, then the portfolio scope switch,
 * search and list (components/Portfolios). Opening and closing the rail is the
 * layout's job (layouts/SidebarLayout).
 *
 * None of these selects calls an API in the legacy app either — they only write
 * shared state (Vuex there, the dashboard slice here) that downstream screens
 * read to pick a costing path and a provider.
 *
 * Same shape as legacy (Cloud.vue): Service Provider is ONE dropdown whose menu
 * has two columns, "Cloud" | "Telemetry Connector", with the logo disclaimer
 * pinned under both. The Telemetry Connector column is left out when it does
 * not apply (OCI, or General IT Average), as legacy does. A connector that can
 * sit on several clouds (Datadog, Prometheus, Billing) adds a "Cloud" field
 * underneath, legacy's `Cloud*`.
 *
 * While a saved portfolio is open the pickers are LOCKED to it (legacy Cloud.vue
 * disables them on `selectedPortfolio`): its instances were costed for that
 * model and provider, so changing either under it would mix the two. The ✕ in
 * the Portfolios header closes it and unlocks them.
 *
 * Not ported yet: legacy also NAVIGATES on a connector pick — to the telemetry
 * page for most, to ?type=billing for Billing. Those pages do not exist here
 * yet; the choice is stored and the cloud behind it drives this page.
 */
import { Dropdown } from '@components/common/Dropdown';
import { useAppMode } from '@variant/useAppMode';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Legacy's GCP billing setup guide (assets/GCP_BigQuery.pdf). Imported as a
// URL: Vite emits it as its own file, fetched only when the link is clicked.
import gcpBillingGuide from '@/assets/docs/GCP_BigQuery.pdf';

import { BENCHMARK_MODEL_ICONS, getBenchmarkModels } from '../benchmarkModels';
import {
  CLOUD_PROVIDERS,
  PROVIDER_DISCLAIMER,
  PROVIDER_DISCLAIMER_LABEL,
  PROVIDER_ICONS,
  cloudsForTelemetry,
  getTelemetryConnectors,
  getTelemetryUnavailableReason,
  isBillingMode,
  isCloudProvider,
  needsCloudChoice,
} from '../serviceProviders';
import {
  requestPortfolio,
  selectOpenPortfolio,
  selectSelectedModel,
  selectSelectedProvider,
  selectSelectedService,
  selectSelectedTelemetry,
  setSelectedModel,
  setSelectedProvider,
  setSelectedTelemetry,
  setTelemetryCloud,
} from '../slice';

import { Portfolios } from './Portfolios';
import styles from './SidebarPanel.module.css';

/** Turn a list of title strings into Dropdown options carrying their logo. */
function toOptions(titles, icons) {
  return titles.map((title) => ({
    value: title,
    label: title,
    icon: icons[title] ? <img src={icons[title]} alt="" /> : undefined,
  }));
}

/** Room for the two columns side by side, connector names unclipped. */
const SERVICE_MENU_WIDTH = 520;

export function SidebarPanel() {
  const dispatch = useDispatch();
  const { mode } = useAppMode();

  const selectedModel = useSelector(selectSelectedModel);
  const selectedProvider = useSelector(selectSelectedProvider);
  const selectedTelemetry = useSelector(selectSelectedTelemetry);
  const selectedService = useSelector(selectSelectedService);
  const locked = Boolean(useSelector(selectOpenPortfolio));

  // CCA gets both models; EIA only SPEC CPU — same filter as legacy getModelItems().
  const modelOptions = useMemo(
    () => toOptions(getBenchmarkModels(mode), BENCHMARK_MODEL_ICONS),
    [mode],
  );

  const telemetryAvailable = !getTelemetryUnavailableReason({
    provider: selectedProvider,
    model: selectedModel,
  });

  // The two menu columns. Billing is CCA-only, as in legacy.
  const serviceGroups = useMemo(
    () => [
      { label: 'Cloud', options: toOptions(CLOUD_PROVIDERS, PROVIDER_ICONS) },
      ...(telemetryAvailable
        ? [
            {
              label: 'Telemetry Connector',
              options: toOptions(getTelemetryConnectors(mode), PROVIDER_ICONS),
            },
          ]
        : []),
    ],
    [mode, telemetryAvailable],
  );

  const telemetryCloudOptions = useMemo(
    () => toOptions(cloudsForTelemetry(selectedTelemetry), PROVIDER_ICONS),
    [selectedTelemetry],
  );

  // Bold heading + body, as legacy renders it. The space between them is
  // explicit: JSX does not supply the whitespace Vue's template handling did.
  const disclaimer = (
    <>
      <strong className={styles.disclaimerLabel}>{PROVIDER_DISCLAIMER_LABEL}</strong>{' '}
      {PROVIDER_DISCLAIMER}
    </>
  );

  /*
   * A Service Provider / Cloud pick empties the instance list, so the PAGE
   * approves it first (slice `requestPortfolio`, kind 'service'): with unsaved
   * work it asks "discard changes?", and only on Yes is `change` dispatched.
   * Legacy (Cloud.vue :488-505) reset the search, safety margin and policy
   * BEFORE asking, so No still lost them — here nothing changes until Yes.
   * Re-picking the current value does nothing (the menu reports it anyway).
   * Model is not asked about, as legacy: it does not touch the list.
   */
  const askChange = (change) => dispatch(requestPortfolio({ kind: 'service', change }));

  // One picker, two kinds of answer: a cloud replaces any connector, a
  // connector brings its default cloud along (see slice.js).
  const onServiceChange = (next) => {
    if (next === selectedService) return;
    askChange(isCloudProvider(next) ? setSelectedProvider(next) : setSelectedTelemetry(next));
  };

  return (
    <aside className={styles.panel} aria-label="Dashboard filters">
      <section className={styles.section}>
        <Dropdown
          id="benchmark-model"
          label="Benchmark / Model"
          required
          options={modelOptions}
          value={selectedModel}
          disabled={locked}
          onChange={(next) => dispatch(setSelectedModel(next))}
        />
      </section>

      <section className={styles.section}>
        <Dropdown
          id="service-provider"
          label="Service Provider"
          groups={serviceGroups}
          columns={serviceGroups.length}
          menuMinWidth={serviceGroups.length > 1 ? SERVICE_MENU_WIDTH : undefined}
          value={selectedService}
          disabled={locked}
          onChange={onServiceChange}
          footer={disclaimer}
        />
      </section>

      {/* Legacy's `Cloud*` field: which cloud the connector's data is on. */}
      {needsCloudChoice(selectedTelemetry) ? (
        <section className={styles.section}>
          <Dropdown
            id="telemetry-cloud"
            label="Cloud"
            options={telemetryCloudOptions}
            value={selectedProvider}
            disabled={locked}
            onChange={(next) => {
              if (next !== selectedProvider) askChange(setTelemetryCloud(next));
            }}
          />
          {/* Legacy Cloud.vue:184-186 — Billing on GCP links the setup guide
              (a PDF, opened in a new tab). */}
          {isBillingMode(selectedTelemetry) && selectedProvider === 'GCP' ? (
            <a
              className={styles.guideLink}
              href={gcpBillingGuide}
              target="_blank"
              rel="noopener noreferrer"
            >
              How to set up GCP billing
            </a>
          ) : null}
        </section>
      ) : null}

      {/* Legacy separates the Cloud block from Portfolios with an <hr>. */}
      <hr className={styles.divider} />

      <section className={styles.section}>
        <Portfolios />
      </section>
    </aside>
  );
}

export default SidebarPanel;
